/// <reference no-default-lib='true' />
/// <reference lib='esnext' />
/// <reference lib='dom' />

export interface ComboboxOption {
  value: string
  label: string
  count?: number
  disabled?: boolean
}

interface ToggleEventLike extends Event {
  newState: 'open' | 'closed'
}

const lastToggledByRootId = new Map<string, string>()

export class ComboboxMultiselect {
  private root: HTMLElement
  private wrapper: HTMLElement
  private input: HTMLInputElement
  private toggle: HTMLButtonElement
  private listbox: HTMLElement
  private status: HTMLElement | null

  private options: ComboboxOption[]
  private selected: Set<string>
  private filtered: ComboboxOption[]
  private activeIndex = -1
  private supportsAnchor: boolean
  private resizeObserver: ResizeObserver | null = null

  private tplTag: HTMLTemplateElement
  private tplCount: HTMLTemplateElement
  private tplOption: HTMLTemplateElement
  private tplEmpty: HTMLTemplateElement
  private tplHiddenInput: HTMLTemplateElement

  private hiddenInputs: HTMLElement
  private name: string

  constructor(root: HTMLElement) {
    const wrapper = root.querySelector<HTMLElement>('.combobox__wrapper')
    const input = root.querySelector<HTMLInputElement>('[role="combobox"]')
    const toggle = root.querySelector<HTMLButtonElement>('.combobox__toggle')
    const listbox = root.querySelector<HTMLElement>('[role="listbox"]')
    const hiddenInputs = root.querySelector<HTMLElement>(
      '.combobox__hidden-inputs',
    )

    const tplTag = root.querySelector<HTMLTemplateElement>(
      'template[data-tpl="tag"]',
    )
    const tplCount = root.querySelector<HTMLTemplateElement>(
      'template[data-tpl="count"]',
    )
    const tplOption = root.querySelector<HTMLTemplateElement>(
      'template[data-tpl="option"]',
    )
    const tplEmpty = root.querySelector<HTMLTemplateElement>(
      'template[data-tpl="empty"]',
    )
    const tplHiddenInput = root.querySelector<HTMLTemplateElement>(
      'template[data-tpl="hidden-input"]',
    )

    const name = root.dataset.name

    if (
      !wrapper || !input || !toggle || !listbox || !hiddenInputs ||
      !tplTag || !tplCount || !tplOption || !tplEmpty || !tplHiddenInput ||
      !name
    ) {
      throw new Error('ComboboxMultiselect: required markup is missing.')
    }

    this.root = root
    this.wrapper = wrapper
    this.input = input
    this.toggle = toggle
    this.listbox = listbox
    this.hiddenInputs = hiddenInputs
    this.name = name
    this.status = root.querySelector<HTMLElement>('[aria-live]')

    this.tplTag = tplTag
    this.tplCount = tplCount
    this.tplOption = tplOption
    this.tplEmpty = tplEmpty
    this.tplHiddenInput = tplHiddenInput

    this.options = this._readOptionsFromDom()
    this.selected = this._readSelectedFromDom()
    this.filtered = [...this.options]

    this.supportsAnchor = CSS.supports('anchor-name', '--test') &&
      CSS.supports('top', 'anchor(bottom)')
    if (!this.supportsAnchor) {
      this.listbox.dataset.fallbackPosition = 'true'
    }

    this._bind()
    this._renderTags()
    this._restoreAfterSwap()
  }

  private _restoreAfterSwap(): void {
    const value = lastToggledByRootId.get(this.root.id)
    if (!value) return
    lastToggledByRootId.delete(this.root.id)
    this.input.focus({ preventScroll: true })
    this.open()
    const i = this.filtered.findIndex((o) => o.value === value)
    if (i !== -1) this._setActive(i)
  }

  isOpen(): boolean {
    return this.listbox.matches(':popover-open')
  }

  open(): void {
    if (this.isOpen()) return
    try {
      this.listbox.showPopover()
    } catch {
      this.listbox.style.display = 'block'
    }
    if (!this.supportsAnchor) this._positionFallback()
  }

  close(): void {
    if (!this.isOpen()) return
    try {
      this.listbox.hidePopover()
    } catch {
      this.listbox.style.display = 'none'
    }
  }

  getSelected(): string[] {
    return [...this.selected]
  }

  private _readOptionsFromDom(): ComboboxOption[] {
    return Array.from(
      this.listbox.querySelectorAll<HTMLElement>('[role="option"]'),
    )
      .map((el) => {
        const rawCount = el.dataset.count
        const count = rawCount != null && rawCount !== ''
          ? Number(rawCount)
          : undefined
        return {
          value: el.dataset.value ?? '',
          label: el.querySelector<HTMLElement>('.combobox__option-label')
            ?.textContent?.trim() ?? '',
          count: count != null && Number.isFinite(count) ? count : undefined,
          disabled: el.getAttribute('aria-disabled') === 'true',
        }
      })
      .filter((o) => o.value)
  }

  private _readSelectedFromDom(): Set<string> {
    return new Set(
      Array.from(
        this.listbox.querySelectorAll<HTMLElement>(
          '[role="option"][aria-selected="true"]',
        ),
      )
        .map((el) => el.dataset.value ?? '')
        .filter((v) => v),
    )
  }

  private _bind(): void {
    this.wrapper.addEventListener('mousedown', (e) => {
      if (e.target === this.wrapper) {
        e.preventDefault()
        this.input.focus()
        this.open()
      }
    })

    this.wrapper.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      const removeBtn = target.closest<HTMLElement>('.combobox__tag-remove')
      if (!removeBtn) return
      e.stopPropagation()
      const tag = removeBtn.closest<HTMLElement>('.combobox__tag')
      if (tag?.dataset.value) {
        this._toggleValue(tag.dataset.value)
        this.input.focus()
      }
    })

    this.toggle.addEventListener('click', (e) => {
      e.preventDefault()
      this.input.focus()
      this.isOpen() ? this.close() : this.open()
    })

    this.input.addEventListener('input', () => {
      this._filter(this.input.value)
      this.open()
    })

    this.input.addEventListener('click', () => {
      this.open()
    })

    this.input.addEventListener('keydown', (e) => this._onKeydown(e))

    this.listbox.addEventListener('click', (e) => {
      const opt = (e.target as HTMLElement).closest<HTMLElement>(
        '[role="option"]',
      )
      if (!opt || !opt.dataset.value) return
      e.preventDefault()
      this._toggleValue(opt.dataset.value)
      this.input.focus()
    })

    this.listbox.addEventListener('mousemove', (e) => {
      const opt = (e.target as HTMLElement).closest<HTMLElement>(
        '[role="option"]',
      )
      if (!opt) return
      const i = this.filtered.findIndex((o) => o.value === opt.dataset.value)
      if (i !== -1 && i !== this.activeIndex) this._setActive(i)
    })

    document.addEventListener('pointerdown', (e) => {
      const target = e.target as Node
      if (!this.root.contains(target) && !this.listbox.contains(target)) {
        this.close()
      }
    })

    if (!this.supportsAnchor) {
      window.addEventListener('scroll', () => this._positionFallback(), true)
      window.addEventListener('resize', () => this._positionFallback())
    }

    this.resizeObserver = new ResizeObserver(() => this._renderTags())
    this.resizeObserver.observe(this.wrapper)

    this.listbox.addEventListener('toggle', (e) => {
      const open = (e as ToggleEventLike).newState === 'open'
      this.input.setAttribute('aria-expanded', String(open))
      this.wrapper.dataset.open = String(open)
      if (!open) {
        this.input.removeAttribute('aria-activedescendant')
        this.activeIndex = -1
        this._clearActive()
      }
    })
  }

  private _onKeydown(e: KeyboardEvent): void {
    const open = this.isOpen()

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          this.open()
          this._setActive(0)
        } else {
          this._setActive(
            Math.min(this.activeIndex + 1, this.filtered.length - 1),
          )
        }
        break

      case 'ArrowUp':
        e.preventDefault()
        if (!open) {
          this.open()
          this._setActive(this.filtered.length - 1)
        } else {
          this._setActive(Math.max(this.activeIndex - 1, 0))
        }
        break

      case 'Home':
        if (open) {
          e.preventDefault()
          this._setActive(0)
        }
        break

      case 'End':
        if (open) {
          e.preventDefault()
          this._setActive(this.filtered.length - 1)
        }
        break

      case 'Enter':
        if (open && this.activeIndex >= 0) {
          e.preventDefault()
          const o = this.filtered[this.activeIndex]
          if (o) this._toggleValue(o.value)
        }
        break

      case ' ':
        if (open && this.activeIndex >= 0 && this.input.value === '') {
          e.preventDefault()
          const o = this.filtered[this.activeIndex]
          if (o) this._toggleValue(o.value)
        }
        break

      case 'Escape':
        if (open) {
          e.preventDefault()
          this.close()
        } else if (this.input.value) {
          this.input.value = ''
          this._filter('')
        }
        break

      case 'Backspace':
        if (this.input.value === '' && this.selected.size > 0) {
          const arr = [...this.selected]
          this._toggleValue(arr[arr.length - 1])
        }
        break

      case 'Tab':
        if (open) this.close()
        break
    }
  }

  private _positionFallback(): void {
    if (!this.isOpen()) return
    const r = this.wrapper.getBoundingClientRect()
    const vh = window.innerHeight
    const spaceBelow = vh - r.bottom
    const spaceAbove = r.top
    const gap = 10
    this.listbox.style.setProperty('--fb-left', `${r.left}px`)
    this.listbox.style.setProperty('--fb-width', `${r.width}px`)
    if (spaceBelow < 200 && spaceAbove > spaceBelow) {
      this.listbox.style.setProperty(
        '--fb-top',
        `${r.top - this.listbox.offsetHeight - gap}px`,
      )
    } else {
      this.listbox.style.setProperty('--fb-top', `${r.bottom + gap}px`)
    }
  }

  private _filter(query: string): void {
    const q = query.toLowerCase().trim()
    this.filtered = this.options.filter((o) =>
      o.label.toLowerCase().includes(q)
    )
    this.activeIndex = -1
    this._renderOptions()
    this._announce(
      this.filtered.length === 0
        ? 'No results'
        : `${this.filtered.length} result${
          this.filtered.length === 1 ? '' : 's'
        } available`,
    )
  }

  private _toggleValue(value: string): void {
    const opt = this.options.find((o) => o.value === value)
    if (!opt || opt.disabled) return
    if (this.selected.has(value)) {
      this.selected.delete(value)
      this._announce(`${opt.label} removed. ${this.selected.size} selected.`)
    } else {
      this.selected.add(value)
      this._announce(`${opt.label} added. ${this.selected.size} selected.`)
    }
    this.input.value = ''
    this._filter('')
    this._renderTags()
    this._renderHiddenInputs()
    lastToggledByRootId.set(this.root.id, value)
    this._emitChange()
  }

  private _emitChange(): void {
    this.root.dispatchEvent(
      new CustomEvent('change', {
        detail: { values: [...this.selected] },
        bubbles: true,
      }),
    )
  }

  private _setActive(i: number): void {
    this.activeIndex = i
    this._clearActive()
    const o = this.filtered[i]
    if (!o) return
    const el = this.listbox.querySelector<HTMLElement>(
      `[data-value="${CSS.escape(o.value)}"]`,
    )
    if (!el) return
    el.classList.add('is-active')
    this.input.setAttribute('aria-activedescendant', el.id)
    el.scrollIntoView({ block: 'nearest' })
  }

  private _clearActive(): void {
    this.listbox
      .querySelectorAll('.is-active')
      .forEach((el) => el.classList.remove('is-active'))
  }

  private _announce(msg: string): void {
    if (this.status) this.status.textContent = msg
  }

  private _renderTags(): void {
    this.wrapper.querySelectorAll('.combobox__tag, .combobox__count').forEach((
      t,
    ) => t.remove())

    const values = [...this.selected]
    if (values.length === 0) return

    const tags: HTMLElement[] = []
    const frag = document.createDocumentFragment()
    for (const value of values) {
      const tag = this._createTag(value)
      if (tag) {
        tags.push(tag)
        frag.appendChild(tag)
      }
    }
    this.wrapper.insertBefore(frag, this.input)

    if (tags.length <= 1 || !this._isOverflowing()) return

    const countEl = this._cloneTemplate(this.tplCount)
    this.wrapper.insertBefore(countEl, tags[0])

    let collapsed = 0
    while (this._isOverflowing() && collapsed < tags.length - 1) {
      tags[collapsed].remove()
      collapsed++
      const labels = values
        .slice(0, collapsed)
        .map((v) => this.options.find((o) => o.value === v)?.label)
        .filter((l): l is string => Boolean(l))
        .join(', ')
      countEl.textContent = `+${collapsed}`
      countEl.title = labels
      countEl.setAttribute(
        'aria-label',
        `${collapsed} more selected: ${labels}`,
      )
    }
  }

  private _isOverflowing(): boolean {
    return this.wrapper.scrollWidth > this.wrapper.clientWidth + 1
  }

  private _createTag(value: string): HTMLElement | null {
    const opt = this.options.find((o) => o.value === value)
    if (!opt) return null

    const tag = this._cloneTemplate(this.tplTag)
    tag.dataset.value = value

    const label = tag.querySelector<HTMLElement>('.combobox__tag-label')
    if (label) label.textContent = opt.label

    const btn = tag.querySelector<HTMLButtonElement>('.combobox__tag-remove')
    if (btn) btn.setAttribute('aria-label', `Remove ${opt.label}`)

    return tag
  }

  private _renderOptions(): void {
    this.listbox.replaceChildren()

    if (this.filtered.length === 0) {
      this.listbox.appendChild(this._cloneTemplate(this.tplEmpty))
      return
    }

    const frag = document.createDocumentFragment()
    for (const opt of this.filtered) {
      const li = this._cloneTemplate(this.tplOption)
      li.id = `${this.listbox.id}-opt-${opt.value}`
      li.setAttribute('aria-selected', String(this.selected.has(opt.value)))
      li.dataset.value = opt.value
      if (opt.disabled) li.setAttribute('aria-disabled', 'true')

      const label = li.querySelector<HTMLElement>('.combobox__option-label')
      if (label) label.textContent = opt.label

      const countEl = li.querySelector<HTMLElement>('.combobox__option-count')
      if (countEl && opt.count != null) {
        li.dataset.count = String(opt.count)
        const dot = countEl.querySelector<HTMLElement>(
          '.combobox__option-count-dot',
        )
        if (dot) dot.textContent = String(opt.count)
        countEl.removeAttribute('hidden')
      } else if (countEl) {
        countEl.remove()
      }

      frag.appendChild(li)
    }
    this.listbox.appendChild(frag)
  }

  private _renderHiddenInputs(): void {
    this.hiddenInputs.replaceChildren()
    const frag = document.createDocumentFragment()
    for (const value of this.selected) {
      const input = this._cloneTemplate<HTMLInputElement>(this.tplHiddenInput)
      input.name = `${this.name}[]`
      input.value = value
      frag.appendChild(input)
    }
    this.hiddenInputs.appendChild(frag)
  }

  private _cloneTemplate<T extends HTMLElement = HTMLElement>(
    tpl: HTMLTemplateElement,
  ): T {
    const clone = tpl.content.cloneNode(true) as DocumentFragment
    const el = clone.firstElementChild
    if (!(el instanceof HTMLElement)) {
      throw new Error('ComboboxMultiselect: template has no element child.')
    }
    return el as T
  }
}

let swapListenerRegistered = false

export function initCombobox() {
  const roots = Array.from(document.querySelectorAll<HTMLElement>('.combobox'))
  for (const root of roots) {
    if (root.dataset.hydrated) continue
    root.dataset.hydrated = 'true'
    new ComboboxMultiselect(root)
  }
  if (!swapListenerRegistered) {
    swapListenerRegistered = true
    globalThis.addEventListener('htmx:afterSwap', () => initCombobox())
  }
}
